// MACHINERY V2 (Stroje) — Fleet V3 body: the dense table view (M2.13).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.13 + desktop/README.md §V3.
// Rendered truth: desktop/screenshots/v3-table--refined-claude.png and
// v3-table--machineid-native.png (both viewed before any code was written).
//
// THIS IS A BODY, NOT A PAGE — the same contract FleetV1Page and FleetV2Page
// keep. FleetPage owns the shell (rail, toolbar, dialogs, readiness popup,
// toast) AND the single data hook, and swaps this component in when
// `fleetView === "V3"`. `useFleetPageData` / `useFleetTelemetry` are never
// called here, so adding a third view added no second poller.
//
// SECTIONS ARRIVE PRE-GROUPED, FROM THE SAME MAP THE OTHER TWO VIEWS RENDER
// (M2.3 ruling (i) — one grouping key, not two). This view sections nothing
// itself and reuses FleetGroupHeader, so the table's per-type group headers ARE
// the grouped view's headers — same counts, same chip vocabulary, same collapse
// keys. A group collapsed in V2 is collapsed here.
//
// SORTING IS PER SECTION, because each section is its own <table>. The ordering
// itself lives in FleetPage (see its `tableSort` note) so a V3 -> V1 -> V3 round
// trip keeps it, and resets with the grouping dimension.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useMemo } from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import FleetGroupHeader from "../FleetGroupHeader";
// Type-only: the three bodies share ONE prop contract (M2.3 ruling (i)); a value
// import would pull the grouped view into this chunk for nothing.
import type { FleetV2PageProps } from "../FleetV2Page";
import { countStates } from "../fleetSelectors";
import { sectionTitle } from "../fleetGroupBy";
import FleetTable from "./FleetTable";
import { FleetTableSort, FleetTableSortColumn, sortMachines } from "./fleetTableSort";

export interface FleetTableViewProps extends FleetV2PageProps {
  sort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
}

const FleetTableView: React.FC<FleetTableViewProps> = ({
  sections, states, telemetry, readiness, newMachineId, collapsedGroups,
  onToggleGroup, buildActions, onOpenProfile, onOpenReadiness, sort, onSortChange,
}) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const lang = getCurrentLanguage();

  // Sorted once per (sections, sort, states, telemetry) rather than on every
  // render: the shell re-renders on each 60 s telemetry tick, and re-sorting
  // every section on an unrelated re-render would be pure waste.
  const sorted = useMemo(
    () =>
      Object.entries(sections).map(
        ([key, list]) =>
          [key, list, sortMachines(list, sort, states, telemetry, lang)] as const
      ),
    [sections, sort, states, telemetry, lang]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
      {sorted.map(([key, list, rows]) => (
        <div key={key} style={{ minWidth: 0 }}>
          {/* Collapse state stays keyed on the RAW group key; only the label is
              translated (see sectionTitle). */}
          {/* marginBottom={0}: the header bar sits FLUSH on the table card, as
              the artboard draws it and as the prop's own doc describes. Without
              it the header falls back to V2's default 10 and floats above
              nothing. Sections stay separated by the parent's gap: 18. */}
          <FleetGroupHeader
            title={sectionTitle(key, translate)}
            machineCount={list.length}
            counts={countStates(list, states)}
            collapsed={collapsedGroups.has(key)}
            onToggle={() => onToggleGroup(key)}
            marginBottom={0}
          />
          {!collapsedGroups.has(key) && (
            <FleetTable
              machines={rows}
              telemetry={telemetry}
              readiness={readiness}
              newMachineId={newMachineId}
              sort={sort}
              onSortChange={onSortChange}
              buildActions={buildActions}
              onOpenProfile={onOpenProfile}
              onOpenReadiness={onOpenReadiness}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default FleetTableView;
